/* THERO Analytics - the ONE sender that turns what shoppers see and tap into Google Analytics events (2026-09-30).
   Spec: docs/superpowers/specs/2026-09-30-analytics-tracking-design.md; the label contract and how to track a new
   section: docs/superpowers/plans/2026-09-30-analytics-tracking.md ("THE CONTRACT"). Unit test:
   node docs/analytics/test_tracker.mjs. Browser proof: python docs/analytics_qc.py.

   How it works
   - Sections label their units in Liquid with data-track-* attributes: product lists + cards, promotions (banners,
     tiles, menu tiles, creator-video cards) and add-to-cart forms that sit outside a card. Features announce the
     rest on one channel: document 'thero:track' {event, params, user} and 'thero:cart-add' {form, source, quantity,
     items}. No feature calls Google directly, so switching tools later changes this one file.
   - Seen = half the unit on screen for 1 second, once per page view (one IntersectionObserver, batched each
     second). The observer respects clipping, so cards scrolled out of a sideways row are not counted.
   - Everything goes through the Google tag that Shopify's Google & YouTube app already runs in this page
     (window.gtag), addressed to the GA4 id only (send_to), so the events share the visitor's own session with the
     app's shop events and GoKwik's purchase. Nothing is sent until that tag is ready; if it never appears (app
     removed, a blocker), this file loads the tag itself after 15 s WITHOUT a page view.
   - Never sends page_view / view_item / add_to_cart / begin_checkout / purchase: Shopify's app and GoKwik already
     send them, and the D2C daily report counts them.
   - Test traffic: theme previews and ?thero_debug=1 (remembered for the tab) mark every event debug_mode, which
     GA4's "Developer traffic" filter removes from reports; the theme editor sends nothing; Shopify's consent API is
     obeyed whenever it is present.
   - Speed (the budget is a MUST): no layout reads on the tap path, one observer, one deferred file.
   - Site version (2026-10-01): every event carries site_version (slash / noslash / offer - Theme settings > THERO
     Site versions) and the visitor gets the user property site_version_seen; snippets/thero-analytics.liquid also
     queues both for the Google app's own events before the tag starts.
   - Offer state (2026-10-09): every event also carries offer_state = <html data-offer-state>, "<pct>|<pair>|<bundle>"
     (e.g. 30|b1g1|799, 20|699|999), so the Buy 2 / Buy 3 test days split cleanly, and the visitor gets the user
     property offer_state_seen (same value) so GoKwik's purchase can be tied to the offer the buyer saw.
   - Every other tap (2026-10-16): a button / summary / in-page link no rule above measures sends ui_click
     {ui_control, ui_section} - see "every other tap" below. A new control is measured by default; give it a clean
     name with data-track-ui="<name>", or data-track-own="<event>" when it sends a better event of its own. */
(function (W) {
  'use strict';
  var D = W.document;
  if (!D) return;
  var SH = W.Shopify || {};
  if (SH.designMode) return;                            // the theme editor: never send
  if (W.THERO_TRACK && W.THERO_TRACK.started) return;   // loaded twice: run once

  var cfg = {};
  try {
    var ce = D.getElementById('thero-analytics-config');
    if (ce) cfg = JSON.parse(ce.textContent || '{}');
  } catch (e) { cfg = {}; }
  var MID = cfg.mid || 'G-GRQ90PPGWV';
  var PAGE = cfg.pageType || 'other';
  var CUR = cfg.currency || 'INR';
  // The live site version this page showed (Theme settings > THERO Site versions: slash / noslash / offer, 2026-10-01).
  var SITEV = cfg.siteVersion || '';
  // The offers this page promised (2026-10-09, the Buy 2 / Buy 3 tests): "<pct>|<pair>|<bundle>" from
  // <html data-offer-state> (layout/theme.liquid), e.g. 30|b1g1|799 or 20|699|999 - so every event can be split by
  // the offer of the day.
  var OFFERS = (D.documentElement && D.documentElement.getAttribute('data-offer-state')) || '';
  // The event look this page wore (dial 2, 29 Oct 2026): none / mega_diwali, from <html data-event-look> - so every
  // event can be split by sale look (GA4 dimension "Event look"; the visitor carries event_look_seen).
  var LOOK = (D.documentElement && D.documentElement.getAttribute('data-event-look')) || '';

  // Shopify's app + GoKwik own these; the theme re-sending any of them would double-count GA4 and the D2C report.
  var BLOCKED = { page_view: 1, view_item: 1, add_to_cart: 1, remove_from_cart: 1, view_cart: 1, begin_checkout: 1,
    add_shipping_info: 1, add_payment_info: 1, purchase: 1, refund: 1 };

  // ---------- test traffic ----------
  var debug = false;
  try {
    if (/[?&]thero_debug=1(&|$)/.test(W.location.search || '')) W.sessionStorage.setItem('thero_debug', '1');
    debug = W.sessionStorage.getItem('thero_debug') === '1';
  } catch (e) { /* storage blocked (private mode): the URL flag alone still works for this page */ }
  if (/[?&]thero_debug=1(&|$)/.test(W.location.search || '')) debug = true;
  if (SH.theme && SH.theme.role && SH.theme.role !== 'main') debug = true;   // a theme preview is never a shopper

  // ---------- pure helpers (also used by docs/analytics/test_tracker.mjs) ----------
  function clip(s, n) { s = s == null ? '' : String(s); return s.length > n ? s.slice(0, n) : s; }
  function userClip(s) { return clip(s, 36); }          // GA4 user-property values stop at 36 characters
  function slug(s) {
    return clip(String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''), 36);
  }
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : undefined; }
  // The Google & YouTube app and the Merchant Center feed both use shopify_ZZ_ for this store (spec D5).
  function itemId(pid, vid) { return 'shopify_ZZ_' + pid + '_' + vid; }
  function attr(el, n) {
    var v = el && el.getAttribute ? el.getAttribute('data-track-' + n) : null;
    return v == null ? '' : v;
  }
  function slotPos(slot) { var m = /:(\d+)$/.exec(slot || ''); return m ? +m[1] : undefined; }

  function itemFromEl(el) {
    var it = {};
    var pid = attr(el, 'pid'), vid = attr(el, 'vid');
    if (pid && vid) it.item_id = itemId(pid, vid);
    var nm = attr(el, 'name'); if (nm) it.item_name = clip(nm, 100);
    var cat = attr(el, 'category'); if (cat) it.item_category = clip(cat, 100);
    var pr = num(attr(el, 'price')); if (pr !== undefined) it.price = pr;
    if (el && el.hasAttribute && el.hasAttribute('data-track-item')) {
      var list = el.closest ? el.closest('[data-track-list]') : null;
      if (list) {
        it.item_list_id = clip(attr(list, 'list'), 100);
        it.item_list_name = clip(attr(list, 'list-name'), 100);
      }
      var ix = num(attr(el, 'index'));
      if (ix === undefined && list && list.querySelectorAll) {
        var all = list.querySelectorAll('[data-track-item]');
        for (var i = 0; i < all.length; i++) { if (all[i] === el) { ix = i + 1; break; } }
      }
      if (ix !== undefined) it.index = ix;
    }
    return it;
  }

  function promoFromEl(el) {
    var it = itemFromEl(el);                              // the featured product, when the unit names one
    var id = attr(el, 'promo');
    if (!it.item_id) {
      // GA4 drops a promotion without items from its Promotions report, so a tile that leads to a collection or
      // page carries a clearly named destination item instead of a product (spec D2).
      var dest = attr(el, 'dest');
      it.item_id = clip(dest ? 'dest_' + dest.replace(/[^a-z0-9]+/gi, '_') : 'promo_' + id, 100);
    }
    if (!it.item_name) it.item_name = clip(attr(el, 'promo-name') || id, 100);
    it.promotion_id = clip(id, 100);
    // empty fields are left out, never sent blank (GA4 would report them as a real "" value)
    var pn = attr(el, 'promo-name'); if (pn) it.promotion_name = clip(pn, 100);
    var cn = attr(el, 'creative'); if (cn) it.creative_name = clip(cn, 100);
    var cs = attr(el, 'slot'); if (cs) it.creative_slot = clip(cs, 100);
    it.location_id = PAGE;
    return it;
  }

  // Where an add to cart came from: the card it sits in, else the promotion, else the form's own label.
  function resolveSource(form, explicit) {
    if (explicit) return { type: clip(explicit, 40), id: explicit === 'compare' ? 'pdp_compare' : '', pos: undefined };
    if (!form || !form.closest) return { type: 'other', id: '', pos: undefined };
    var card = form.closest('[data-track-item]');
    if (card) {
      var list = card.closest('[data-track-list]');
      return { type: 'list', id: list ? attr(list, 'list') : '', pos: itemFromEl(card).index };
    }
    var promo = form.closest('[data-track-promo]');
    if (promo) return { type: attr(promo, 'kind') || 'promotion', id: attr(promo, 'promo'), pos: slotPos(attr(promo, 'slot')) };
    var src = form.closest('[data-track-source]');
    if (src) {
      // the video player's form: the dialog says which video is showing (assets/thero-creator-videos.js sets it)
      var holder = form.closest('[data-track-source-id]');
      return {
        type: attr(src, 'source'),
        id: holder ? attr(holder, 'source-id') : '',
        pos: holder ? num(attr(holder, 'source-pos')) : undefined
      };
    }
    return { type: 'other', id: '', pos: undefined };
  }

  // ---------- sending ----------
  var Q = [];                 // commands waiting for the Google tag
  var log = [];               // debug only: the last events sent (read by docs/analytics_qc.py)
  var ready = false, fellBack = false, t0 = Date.now(), poll = null;

  function tagReady() {
    return typeof W.gtag === 'function' && !!(W.google_tag_manager && W.google_tag_manager[MID]);
  }
  function allowed() {
    var cp = W.Shopify && W.Shopify.customerPrivacy;
    try {
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
    } catch (e) { /* a consent API that throws is treated as absent */ }
    return true;   // India: Shopify's default outside consent regions is "allow all processing" (spec D10)
  }
  function flush() {
    if (!ready && !tagReady()) return false;
    ready = true;
    while (Q.length) {
      var c = Q.shift();
      try { W.gtag.apply(W, c); } catch (e) { /* a tag error must never break the page */ }
    }
    return true;
  }
  /* A tap never waits for Google's tag (INP - the speed budget is a MUST). Measured 2026-10-01 with
     docs/analytics/inp_probe.py (phone, 4x CPU, quiet page): sending inside the tap made opening a creator video
     +97 ms slower (the tag's own work on 2 events + 2 labels). So every command waits in the queue and is handed to
     the tag after the next paint (requestAnimationFrame, then a new task); pagehide / hidden flush synchronously, so a
     tap that leaves the page still sends. */
  var flushQueued = false;
  function scheduleFlush() {
    if (flushQueued) return;
    flushQueued = true;
    var run = function () { flushQueued = false; flush(); };
    if (typeof W.requestAnimationFrame === 'function') W.requestAnimationFrame(function () { W.setTimeout(run, 0); });
    else W.setTimeout(run, 0);
  }
  function push(cmd) {
    Q.push(cmd);
    if (Q.length > 200) Q.shift();   // a page left open without the tag never grows without bound
    scheduleFlush();
  }
  function send(name, params) {
    if (!name || BLOCKED[name] || !allowed()) return;
    var p = {};
    for (var k in params) {
      if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
      var v = params[k];
      if (v === undefined || v === null || v === '') continue;
      p[clip(k, 40)] = typeof v === 'string' ? clip(v, 100) : v;
    }
    p.page_type = PAGE;
    if (SITEV) p.site_version = SITEV;   // every event can be split by site version (the price tests)
    if (OFFERS) p.offer_state = OFFERS;  // ... and by the offers of the day (the Buy 2 / Buy 3 tests)
    if (LOOK) p.event_look = LOOK;       // ... and by the event look (29 Oct 2026, Mega Diwali)
    p.send_to = MID;
    if (debug) {
      p.debug_mode = true;
      log.push([name, p]);
      if (log.length > 50) log.shift();
    }
    push(['event', name, p]);
  }
  function setUser(props) {
    if (!props || !allowed()) return;
    var p = {}, any = false;
    for (var k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      if (props[k] == null || props[k] === '') continue;
      p[clip(k, 24)] = userClip(props[k]);
      any = true;
    }
    if (any) push(['set', 'user_properties', p]);
  }

  function fallback() {
    if (fellBack || tagReady()) return;
    fellBack = true;
    W.dataLayer = W.dataLayer || [];
    if (typeof W.gtag !== 'function') W.gtag = function () { W.dataLayer.push(arguments); };
    W.gtag('js', new Date());
    W.gtag('config', MID, { send_page_view: false });   // never a second page view (Shopify's app sends it)
    var s = D.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(MID);
    (D.head || D.documentElement).appendChild(s);
    ready = true;                                        // the shim above holds commands until gtag.js arrives
    flush();
  }
  function stopPoll() { if (poll) { W.clearInterval(poll); poll = null; } }
  function tick() {
    if (flush()) { stopPoll(); return; }
    if (Date.now() - t0 > 15000) { fallback(); stopPoll(); }
  }

  // ---------- seen ----------
  var SEL = '[data-track-item],[data-track-promo]';
  var watched = new WeakSet(), seen = new WeakSet(), timers = new Map();
  var pendLists = {}, pendPromos = [], flushT = null;

  function chunk(arr, n) { var out = []; for (var i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; }
  function markSeen(el) {
    if (seen.has(el)) return;
    seen.add(el);
    if (el.hasAttribute('data-track-item')) {
      var list = el.closest('[data-track-list]');
      if (!list) return;
      var id = attr(list, 'list');
      (pendLists[id] = pendLists[id] || { name: attr(list, 'list-name'), items: [] }).items.push(itemFromEl(el));
    } else if (el.hasAttribute('data-track-promo')) {
      pendPromos.push(promoFromEl(el));
    }
    if (!flushT) flushT = W.setTimeout(flushSeen, 1000);
  }
  function flushSeen() {
    if (flushT) { W.clearTimeout(flushT); flushT = null; }
    Object.keys(pendLists).forEach(function (id) {
      var L = pendLists[id];
      chunk(L.items, 10).forEach(function (items) {
        send('view_item_list', { item_list_id: id, item_list_name: L.name, items: items });
      });
    });
    pendLists = {};
    chunk(pendPromos, 10).forEach(function (items) { send('view_promotion', { items: items }); });
    pendPromos = [];
  }
  var io = ('IntersectionObserver' in W) ? new W.IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var el = e.target;
      if (seen.has(el)) return;
      if (e.isIntersecting && e.intersectionRatio >= 0.5) {
        if (!timers.has(el)) {
          timers.set(el, W.setTimeout(function () {
            timers.delete(el);
            if (io) io.unobserve(el);
            markSeen(el);
          }, 1000));
        }
      } else if (timers.has(el)) {
        W.clearTimeout(timers.get(el));
        timers.delete(el);
      }
    });
  }, { threshold: [0, 0.5] }) : null;
  function watch(el) { if (!io || watched.has(el)) return; watched.add(el); io.observe(el); }
  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SEL)) watch(root);
    var els = root.querySelectorAll(SEL);
    for (var i = 0; i < els.length; i++) watch(els[i]);
  }

  // ---------- tapped ----------
  function onClick(ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-track-ignore]')) return;
    var unit = t.closest(SEL);
    if (!unit) return;
    var f = t.closest('form');
    if (f && unit.contains(f)) return;                  // add to cart inside a card is measured by thero:cart-add
    var now = Date.now();
    if (unit.__trackT && now - unit.__trackT < 800) return;   // one tap, one event
    unit.__trackT = now;
    if (!seen.has(unit)) { markSeen(unit); flushSeen(); }   // a tap proves it was seen: click rate stays <= 100%
    if (unit.hasAttribute('data-track-item')) {
      var it = itemFromEl(unit);
      send('select_item', { item_list_id: it.item_list_id, item_list_name: it.item_list_name, items: [it] });
      setUser({ last_click: 'list:' + (it.item_list_id || '') + ':' + (it.index || '') });
    } else {
      var pr = promoFromEl(unit);
      send('select_promotion', {
        promotion_id: pr.promotion_id, promotion_name: pr.promotion_name,
        creative_name: pr.creative_name, creative_slot: pr.creative_slot, items: [pr]
      });
      setUser({ last_click: (attr(unit, 'kind') || 'promo') + ':' + pr.promotion_id });
    }
  }

  // ---------- every other tap (2026-10-16) ----------
  /* Shivam, 16 Oct 2026: "the complete routine and all buttons ... in all that, I need tracking ... make it a rule".
     Before this, 76 tappable controls on a product page sent nothing (review filters + pages, photo arrows, ingredient
     bubbles, accordions, the video player's buttons - docs/analytics/control_coverage.py). Now a control that no
     other rule measures sends ui_click {ui_control, ui_section}, so a new button can never ship with no data.
     - Left to their own, better event: product cards + promotions (select_item / select_promotion), marketplace logos
       (marketplace_click), pack rows (pack_select), quantity selectors (qty_plus / qty_minus), add-to-cart submits
       (thero_cart_add) and anything marked data-track-own="<its event>". data-track-ignore = never counted.
     - Name: data-track-ui, else aria-label, else title, else its text - read with textContent (innerText would force
       a layout inside the tap: the speed budget). Lower-case, every number turned into n ("page_n", "slide_n"), so
       a count or a customer's digits never leave the page and one control keeps one name.
     - Section: data-track-area, else the Shopify section key (the part after "__" in its id), else dialog / header.
     - Sent like every event: queued, handed to the Google tag after the next paint. */
  var UI_SEL = 'button,summary,[role="button"],a[href^="#"],[data-track-ui]';
  var UI_OWNED = '[data-track-item],[data-track-promo],[data-thero-marketplace],[data-tpack-opt],[data-tqs],[data-track-own],[data-track-ignore]';
  function uiName(s) {
    return clip(String(s == null ? '' : s).toLowerCase().replace(/[0-9]+/g, ' n ').replace(/[^a-z]+/g, '_').replace(/^_+|_+$/g, ''), 40);
  }
  function uiSection(el) {
    var a = el.closest('[data-track-area]');
    if (a) return clip(a.getAttribute('data-track-area'), 40);
    var s = el.closest('.shopify-section');
    if (s && s.id) {
      var id = s.id.replace(/^shopify-section-/, '');
      var i = id.lastIndexOf('__');
      return clip(i >= 0 ? id.slice(i + 2) : id, 40);
    }
    if (el.closest('dialog')) return 'dialog';
    if (el.closest('header')) return 'header';
    return 'page';
  }
  function uiControl(t) {
    var el = t && t.closest ? t.closest(UI_SEL) : null;
    if (!el || el.disabled || el.getAttribute('aria-disabled') === 'true') return null;
    if (el.closest(UI_OWNED)) return null;
    if (el.type === 'submit' && el.form && /\/cart\/add/.test(el.form.getAttribute('action') || '')) return null;
    var name = el.getAttribute('data-track-ui') || el.getAttribute('aria-label') || el.getAttribute('title') || el.textContent;
    return { ui_control: uiName(name) || 'unnamed', ui_section: uiSection(el) };
  }
  function onUi(ev) {
    var c = uiControl(ev.target);
    if (!c) return;
    var el = ev.target.closest(UI_SEL);
    var now = Date.now();
    if (el.__uiT && now - el.__uiT < 400) return;       // one tap, one event
    el.__uiT = now;
    send('ui_click', c);
  }

  // ---------- the channel ----------
  D.addEventListener('thero:track', function (ev) {
    var d = (ev && ev.detail) || {};
    if (!d.event || BLOCKED[d.event]) return;
    if (d.user) setUser(d.user);                        // set first, so this very event carries the label
    send(d.event, d.params || {});
  });
  D.addEventListener('thero:cart-add', function (ev) {
    var d = (ev && ev.detail) || {};
    var src = resolveSource(d.form || null, d.source || null);
    var qty = num(d.quantity) || 1;
    (d.items || []).forEach(function (x) {
      if (!x) return;
      var price = num(x.price);                          // Shopify's cart answers in paise
      var q = num(x.quantity) || qty;                    // a pack (16 Oct 2026) carries each item's own unit count
      send('thero_cart_add', {
        source_type: src.type, source_id: src.id, source_position: src.pos,
        product_handle: x.handle,
        item_id: (x.product_id && x.variant_id) ? itemId(x.product_id, x.variant_id) : undefined,
        quantity: q,
        value: price !== undefined ? Math.round(price * q) / 100 : undefined,
        currency: CUR,
        pack_type: d.pack || undefined                   // which "Choose your pack" option was added (GA4 "Pack type")
      });
    });
  });

  // ---------- start ----------
  function init() {
    // the snippet already queued this for the Google app's own events; repeating it here keeps it right even if
    // the app ever replaces the tag's command list
    if (SITEV) setUser({ site_version_seen: SITEV });
    // the offers of the day on the VISITOR too (2026-10-09): GoKwik's purchase arrives without our event params, so
    // this user-scoped field is what ties a purchase to the offer state the buyer last saw (GA4 "Offer state seen")
    if (OFFERS) setUser({ offer_state_seen: OFFERS });
    if (LOOK) setUser({ event_look_seen: LOOK });
    scan(D.body || D.documentElement);
    // late markup: search results, re-rendered drawers, "load more" pages
    if ('MutationObserver' in W && D.body) {
      new W.MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes || [];
          for (var j = 0; j < added.length; j++) { if (added[j].nodeType === 1) scan(added[j]); }
        }
      }).observe(D.body, { childList: true, subtree: true });
    }
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', init); else init();
  D.addEventListener('click', onClick, true);
  D.addEventListener('click', onUi, true);
  W.addEventListener('pagehide', function () { flushSeen(); flush(); });
  D.addEventListener('visibilitychange', function () { if (D.visibilityState === 'hidden') { flushSeen(); flush(); } });
  poll = W.setInterval(tick, 300);
  tick();

  W.THERO_TRACK = {
    version: '2026-10-16',
    siteVersion: SITEV,
    offerState: OFFERS,
    eventLook: LOOK,
    started: true,
    log: log,
    _t: {
      clip: clip, userClip: userClip, slug: slug, itemId: itemId, itemFromEl: itemFromEl, promoFromEl: promoFromEl,
      resolveSource: resolveSource, BLOCKED: BLOCKED, tick: tick, debug: debug,
      uiControl: uiControl, uiName: uiName, onUi: onUi,
      queued: function () { return Q.length; }
    }
  };
})(window);
